import { Autocomplete, Box, InputBase, createStyles } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { customStyle, theme } from './CustomAutocompleteStyle';
import * as React from 'react';

import { ThemeProvider } from '@mui/material/styles';

const useStyles = makeStyles(() => createStyles(customStyle));

export const CustomAutocomplete = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    backgroundColor: '#A89DF7',
  }));

  const GroupItems = styled('ul')({
    padding: 0,
  });

  return (
    <ThemeProvider theme={theme}>
      <Autocomplete
        options={props.list || []}
        disabled={props.disabled}
        defaultValue={props.value ?? null}
        isOptionEqualToValue={
          props.isOptionEqualToValue ||
          ((option, value) => option === value)
        }
        value={props.value ?? null}
        noOptionsText={t("Pas d'options")}
        getOptionLabel={(option) => (option ? props.getOptionLabel(option) : '')}
        fullWidth
        classes={{
          option: classes.option,
          clearIndicatorDirty: classes.clearIndicator,
          popupIndicator: classes.popupIndicator,
        }}
        className={`${
          props.disabled ? classes.textFieldDisabled : classes.textField
        }`}
        onChange={(event, value) => props.setValue(value)}
        getOptionDisabled={(option) => (props.getOptionDisabled ? props.getOptionDisabled(option) : false)}
        groupBy={(option) => option.availabilityGroup}
        renderGroup={(params) => {
          if (props.isWriterList) {
            return (
              <li key={params.key}>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            );
          } else {
            return (
              <div key={params.key}>
                <div>{params.group}</div>
                <div>{params.children}</div>
              </div>
            );
          }
        }}
        renderOption={(_props, option, state) => {
          let optionStyle = {};
          if (props.isWriterList) {
            const isAvailable = option.isAvailable;
            optionStyle = {
              color: isAvailable ? 'inherit' : 'gray',
            };
          }

          const endAdornment = typeof props.renderOptionEnd === 'function' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {props.renderOptionEnd(option)}
            </Box>
          ) : null;

          return (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                ...optionStyle,
              }}
              {..._props}
            >
              <Box>
                {typeof props.renderOptionLabel === 'function'
                  ? props.renderOptionLabel(option)
                  : props.getOptionLabel(option)}
              </Box>
              <Box>{endAdornment}</Box>
            </Box>
          );
        }}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, ...rest } = params;
          const mergedInputProps = {
            ...params.InputProps,
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {typeof props.renderInputEnd === 'function'
                  ? props.renderInputEnd()
                  : props.renderInputEnd}
                {params.InputProps?.endAdornment}
              </Box>
            ),
          };
          return (
            <InputBase
              classes={{ root: classes.root }}
              variant="outlined"
              placeholder={props.placeholder || ''}
              {...mergedInputProps}
              {...rest}
            />
          );
        }}
      />
    </ThemeProvider>
  );
};

export const CustomAutocompleteFreeSolo = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <ThemeProvider theme={theme}>
      <Autocomplete
        freeSolo
        options={props.list || []}
        isOptionEqualToValue={
          props.isOptionEqualToValue ||
          ((option, value) => option === value)
        }
        getOptionLabel={(option) => (option ? props.getOptionLabel(option) : '')}
        noOptionsText={t("Pas d'options")}
        value={props.value ?? null}
        onInputChange={(event, value) => props.onInputChange(value)}
        onChange={(event, value) => props.setValue(value)}
        fullWidth
        classes={{
          option: classes.option,
          clearIndicatorDirty: classes.clearIndicator,
          popupIndicator: classes.popupIndicator,
        }}
        className={`${
          props.disabled ? classes.textFieldDisabled : classes.textField
        }`}
        renderOption={(_props, option, state) => {
          const isAvailable = option.isAvailable;
          const optionStyle = {
            color: isAvailable ? 'inherit' : 'gray',
          };
          return (
            <span {..._props} style={optionStyle}>
              {props.getOptionLabel(option)}
            </span>
          );
        }}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, ...rest } = params;
          return (
            <InputBase
              classes={{ root: classes.root }}
              variant="outlined"
              placeholder={props.placeholder || ''}
              {...params.InputProps}
              {...rest}
            />
          );
        }}
      />
    </ThemeProvider>
  );
};

export const CustomAutocompleteDisabled = (props) => {
  const classes = useStyles();

  return (
    <ThemeProvider theme={theme}>
      <Autocomplete
        readOnly
        disableClearable
        options={props.list}
        getOptionLabel={(option) => props.getOptionLabel(option)}
        value={props.value}
        onChange={(event, value) => props.setValue(value)}
        fullWidth
        classes={{
          option: classes.option,
          clearIndicatorDirty: classes.clearIndicator,
          popupIndicator: classes.popupIndicator,
        }}
        className={`${
          props.disabled ? classes.textFieldDisabled : classes.textField
        }`}
        renderOption={(_props, option, state) => {
          return <span {..._props}>{props.getOptionLabel(option)}</span>;
        }}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, ...rest } = params;
          return (
            <InputBase
              readOnly
              classes={{ root: classes.root }}
              variant="outlined"
              {...params.InputProps}
              {...rest}
            />
          );
        }}
      />
    </ThemeProvider>
  );
};
